import React, { ReactElement, useRef, useState, useContext, useEffect, memo } from 'react';

import ContactFlyoutContext from 'components/ContactFlyout/ContactFlyoutContext';
import Icon from 'components/Icon/Icon';
import { useTabTrap } from 'hooks';

import { Container, StepButton, Content, StyledForm } from './ContactFlyoutForm.styled';
import ContactFlyoutFormProps from './ContactFlyoutFormProps';

const ContactFlyoutForm = ({ heading, icon, form }: ContactFlyoutFormProps): ReactElement => {
    const [showStep, setShowStep] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const stepButtonRef = useRef<HTMLButtonElement>(null);
    const { setIsStepActive, open, setStepHeight } = useContext(ContactFlyoutContext);

    useTabTrap(containerRef, showStep);

    useEffect(() => {
        setIsStepActive(showStep);
    }, [showStep, setIsStepActive]);

    useEffect(() => {
        if (!open) {
            setShowStep(false); // Reset step only on close
        }
    }, [open]);

    useEffect(() => {
        if (showStep && contentRef.current && stepButtonRef.current) {
            const height = contentRef.current.offsetHeight + stepButtonRef.current.offsetHeight;
            setStepHeight(height);
        } else {
            setStepHeight(undefined);
        }
    }, [showStep, setStepHeight]);

    return (
        <Container $isOpen={showStep} ref={containerRef}>
            <StepButton
                $isOpen={showStep}
                actionTheme="black"
                icon={showStep ? 'bigArrowLeft28' : icon}
                aria-expanded={showStep}
                onClick={() => setShowStep(!showStep)}
                ref={stepButtonRef}
                gtmClickType="contact-flyout-cta"
            >
                {heading}
                {!showStep && <Icon icon="arrowRight28" />}
            </StepButton>
            {form && (
                <Content ref={contentRef} aria-hidden={!showStep} aria-live="polite" style={{ display: showStep ? 'block' : 'none' }}>
                    {form?.formElements && (
                        <StyledForm
                            id={form.formId}
                            action={form.postUrl}
                            items={form.formElements}
                            spamTrapAction={form.spamTrapUrl}
                            size={'small'}
                            identifier={`contact-flyout-form-${form.identifier}`}
                            areaName="ContactFlyoutForm"
                            gtmFormType={form.gtmFormType}
                            formActors={form.formActors}
                            l18n={form.l18n}
                        />
                    )}
                </Content>
            )}
        </Container>
    );
};

export default memo(ContactFlyoutForm);
